import { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { fetchWikiPages } from "../services/wiki.service";
import type { WikiPageSummary } from "../types/wiki.types";

/**
 * Cascading folder picker for the Notes tree — one dropdown per depth level,
 * joined by "/", instead of a single flat list of every folder in the org.
 * Picking a folder reveals a new dropdown for its children; changing a
 * shallower pick clears everything deeper than it.
 */
export default function NotesFolderPicker({
  value,
  onChange,
  label = "Folder",
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  const [allItems, setAllItems] = useState<WikiPageSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchWikiPages()
      .then((items) => {
        if (!cancelled) setAllItems(items || []);
      })
      .catch(() => {
        if (!cancelled) setAllItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const folders = useMemo(() => allItems.filter((i) => i.type === "folder"), [allItems]);
  const folderById = useMemo(() => new Map(folders.map((f) => [f._id, f])), [folders]);

  // Reconstruct the chain of folder ids from root down to `value`, so
  // reopening an already-configured node shows the right chain of dropdowns
  // instead of resetting to root every time.
  const path = useMemo(() => {
    if (!value) return [] as string[];
    const chain: string[] = [];
    let current = folderById.get(value);
    while (current) {
      chain.unshift(current._id);
      current = current.parentId ? folderById.get(current.parentId) : undefined;
    }
    return chain;
  }, [value, folderById]);

  const childrenOf = (parentId: string | null) =>
    folders.filter((f) => (f.parentId ?? null) === parentId).sort((a, b) => a.title.localeCompare(b.title));

  // One dropdown per already-chosen level, plus a trailing dropdown for the
  // next level down whenever the deepest selection still has children.
  const levels: { parentId: string | null; options: WikiPageSummary[] }[] = [];
  let cursor: string | null = null;
  for (const id of path) {
    levels.push({ parentId: cursor, options: childrenOf(cursor) });
    cursor = id;
  }
  const trailing = childrenOf(cursor);
  if (trailing.length > 0 || levels.length === 0) {
    levels.push({ parentId: cursor, options: trailing });
  }

  const handleSelect = (levelIndex: number, folderId: string) => {
    if (!folderId) {
      onChange(levelIndex === 0 ? "" : path[levelIndex - 1]);
      return;
    }
    onChange(folderId);
  };

  if (loading) {
    return <TextField size="small" fullWidth label={label} disabled placeholder="Loading folders…" />;
  }

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
        {levels.map((level, index) => (
          <Box key={level.parentId ?? "root"} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {index > 0 && (
              <Typography color="text.secondary" variant="body2">
                /
              </Typography>
            )}
            <TextField
              select
              size="small"
              value={path[index] ?? ""}
              onChange={(e) => handleSelect(index, e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">{index === 0 ? "Root" : "— none —"}</MenuItem>
              {level.options.map((f) => (
                <MenuItem key={f._id} value={f._id}>
                  {f.title}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
